import { Link } from "react-router-dom";
import { Phone, MessageCircle, Clock, Star, ShieldCheck, Truck, ThumbsUp, MapPin } from "lucide-react";
import { COMPANY, SERVICES, BAIRROS, TESTIMONIALS, bairroUrl, heroImage, bairrosImage, waLink } from "@/data/site";
import { SEO } from "@/components/SEO";
import { ServiceCard } from "@/components/ServiceCard";
import { CTABanner } from "@/components/CTABanner";
import { FAQAccordion } from "@/components/FAQAccordion";

const homeFaq = [
  { q: "Quanto custa para desentupir?", a: "O valor depende do tipo de entupimento e da tubulação. Fazemos a avaliação no local e passamos o orçamento antes de iniciar — sempre transparente, sem surpresas." },
  { q: "Em quanto tempo a equipe chega?", a: "Nos bairros nobres de São Paulo, nossa média de chegada é de 15 a 30 minutos, 24 horas por dia." },
  { q: "Vocês quebram parede ou piso?", a: "Na grande maioria dos casos, não. Usamos máquinas rotativas e hidrojateamento que liberam a tubulação sem quebra-quebra." },
  { q: "O serviço tem garantia?", a: "Sim. Todos os nossos serviços têm garantia. Se o problema persistir, retornamos sem custo adicional." },
  { q: "Atendem finais de semana e feriados?", a: "Sim! Funcionamos 24 horas por dia, 7 dias por semana, incluindo feriados." },
];

export default function Home() {
  return (
    <>
      <SEO
        title="Desentupidora 24h em São Paulo | Desentupidora Paulistana"
        description="Desentupidora 24 horas nos bairros nobres de São Paulo: Jardins, Itaim Bibi, Moema, Higienópolis, Morumbi e região. Atendimento imediato, sem quebra-quebra. Ligue (11) 97195-0197."
        path="/"
      />

      {/* HERO */}
      <section className="bg-brand-black text-white relative overflow-hidden grain-overlay" data-testid="hero-section">
        <div className="max-w-7xl mx-auto container-px py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 bg-brand-red text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-6">
              <MapPin className="w-4 h-4" /> Bairros nobres de São Paulo
            </span>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5 text-justify">
              Desentupimento<br />
              de <span className="text-brand-yellow">alto padrão</span><br />
              em São Paulo
            </h1>
            <p className="text-gray-300 text-base sm:text-lg max-w-xl mb-8">
              Desentupidora 24h nos bairros nobres de São Paulo — Jardins, Itaim Bibi, Moema, Higienópolis, Morumbi e região. Rapidez, higiene e garantia.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <a href={`tel:${COMPANY.phoneTel}`} className="btn-red text-base" data-testid="hero-phone-btn">
                <Phone className="w-5 h-5" /> {COMPANY.phoneDisplay}
              </a>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-green text-base" data-testid="hero-whatsapp-btn">
                <MessageCircle className="w-5 h-5" /> Chamar no WhatsApp
              </a>
            </div>
            <div className="flex items-center gap-5 text-sm text-gray-400">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-wa-green" /> Garantia</span>
              <span className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-wa-green" /> Sem quebra-quebra</span>
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-brand-yellow" fill="currentColor" /> Nota 5,0</span>
            </div>
          </div>

          <div className="relative">
            <img
              src={heroImage}
              alt="Técnico da Desentupidora Paulistana pronto para atendimento 24h"
              className="w-full max-w-md mx-auto rounded-xl object-cover"
            />
            <div className="absolute -top-2 right-2 lg:right-10 grid place-items-center w-24 h-24 rounded-full bg-wa-green text-white text-center shadow-lg shadow-wa-green/40">
              <div>
                <Clock className="w-6 h-6 mx-auto mb-0.5" />
                <span className="block font-black text-lg leading-none">24</span>
                <span className="block text-[10px] font-bold uppercase tracking-wide">Horas</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-brand-red text-white" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto container-px py-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { icon: Clock, t: "24h por dia", s: "Todos os dias" },
            { icon: Truck, t: "15 min", s: "Tempo de chegada" },
            { icon: ThumbsUp, t: "+15 anos", s: "De experiência" },
            { icon: ShieldCheck, t: "Garantia", s: "Em todo serviço" },
          ].map((i, idx) => (
            <div key={idx} className="flex flex-col items-center gap-1">
              <i.icon className="w-6 h-6 mb-1" />
              <span className="font-display font-extrabold text-lg leading-none">{i.t}</span>
              <span className="text-xs text-white/80">{i.s}</span>
            </div>
          ))}
        </div>
      </section>

      {/* SERVIÇOS */}
      <section className="bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto container-px py-16 lg:py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="section-label">O que fazemos</span>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-brand-black">Nossos Serviços</h2>
            <p className="text-gray-600 mt-3">Desentupimento e limpeza de tubulações para residências, condomínios e empresas — com equipamentos profissionais.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <ServiceCard key={s.slug} service={s} />
            ))}
          </div>
        </div>
      </section>

      {/* FAIXA DE BAIRROS */}
      <section className="bg-brand-light border-y border-gray-200" data-testid="bairros-strip">
        <div className="max-w-7xl mx-auto container-px py-5">
          <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
            {BAIRROS.map((b) => (
              <Link key={b.slug} to={bairroUrl(b.slug)} className="flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-brand-gray hover:text-brand-red transition-colors px-2">
                <MapPin className="w-4 h-4 text-brand-red" /> {b.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BAIRROS ATENDIDOS DETALHADO */}
      <section className="bg-white" data-testid="bairros-section">
        <div className="max-w-7xl mx-auto container-px py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <img src={bairrosImage} alt="Técnico da Paulistana atendendo cliente em bairro nobre de São Paulo" loading="lazy" className="rounded-xl w-full object-cover shadow-lg" />
          </div>
          <div className="order-1 lg:order-2">
            <span className="section-label">Cobertura</span>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-brand-black mb-4">
              Atendemos os principais bairros nobres de São Paulo
            </h2>
            <p className="text-gray-600 mb-6">Estrutura dedicada às regiões premium da capital, com equipes próximas para chegar rápido até você.</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
              {BAIRROS.map((b) => (
                <Link key={b.slug} to={bairroUrl(b.slug)} className="flex items-center gap-2 text-sm text-brand-black hover:text-brand-red transition-colors group">
                  <MapPin className="w-3.5 h-3.5 text-brand-red" />
                  <span className="group-hover:underline">{b.name}</span>
                </Link>
              ))}
            </div>
            <Link to="/bairros.html" className="btn-red mt-7 text-sm">Ver todas as regiões</Link>
          </div>
        </div>
      </section>

      {/* SOBRE */}
      <section className="bg-brand-light" data-testid="sobre-section">
        <div className="max-w-7xl mx-auto container-px py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="section-label">Quem somos</span>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-brand-black mb-5">
              Sobre a Desentupidora Paulistana — compromisso e excelência
            </h2>
            <p className="text-gray-600 mb-4">
              Há mais de 15 anos atuando nos bairros nobres de São Paulo, a Desentupidora Paulistana construiu sua reputação na rapidez, na higiene e na transparência. Somos especialistas em desentupimento residencial, comercial e industrial.
            </p>
            <p className="text-gray-600 mb-6">
              Trabalhamos com equipamentos modernos — máquinas rotativas e hidrojateamento — para resolver desde a pia da cozinha até colunas de esgoto de grandes edifícios, sempre com profissionais uniformizados e orçamento claro.
            </p>
            <div className="grid grid-cols-3 gap-4">
              {[
                { n: "+15", l: "Anos de experiência" },
                { n: "20", l: "Bairros atendidos" },
                { n: "24h", l: "Plantão diário" },
              ].map((i, idx) => (
                <div key={idx} className="bg-white rounded-lg p-4 text-center shadow-sm">
                  <span className="block font-display font-black text-2xl text-brand-red">{i.n}</span>
                  <span className="block text-xs text-gray-500 mt-1">{i.l}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={SERVICES[3].image} alt="Equipe Paulistana em ação" loading="lazy" className="rounded-xl w-full object-cover shadow-lg" />
            <div className="absolute -bottom-5 -left-3 sm:left-5 bg-brand-red text-white rounded-lg px-5 py-4 shadow-xl">
              <span className="block font-display font-black text-3xl leading-none">+15</span>
              <span className="block text-xs font-bold uppercase tracking-wide mt-1">Anos de experiência</span>
            </div>
          </div>
        </div>
      </section>

      <CTABanner />

      {/* PROBLEMAS / SOLUÇÕES + FAQ */}
      <section className="bg-white" data-testid="faq-section">
        <div className="max-w-7xl mx-auto container-px py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <img src={SERVICES[0].image} alt="Sifão cromado sob a pia sendo inspecionado" loading="lazy" className="rounded-xl w-full object-cover shadow-lg" />
          </div>
          <div>
            <span className="section-label">Dúvidas frequentes</span>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-brand-black mb-6">
              Desentupimos qualquer tipo de tubulação
            </h2>
            <FAQAccordion items={homeFaq} testIdPrefix="home-faq" />
          </div>
        </div>
      </section>

      {/* DEPOIMENTOS */}
      <section className="bg-brand-light" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto container-px py-16 lg:py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="section-label">Quem confia</span>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-brand-black">O que dizem nossos clientes</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white rounded-lg p-6 shadow-sm" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-brand-yellow" fill="currentColor" />
                  ))}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">"{t.text}"</p>
                <div>
                  <span className="block font-bold text-sm text-brand-black">{t.name}</span>
                  <span className="block text-xs text-gray-500">{t.bairro}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
